import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { apiService } from '../service/api'
import BoardLayout from '@/components/layout/BoardLayout'
import { useNavigateBack } from '@/hooks/useNavigateBack'
import type { DocumentRecord } from '@/types/catalog'
import ChatInterface from '@/components/ChatInterface'
import AiChatButton from '@/components/AiChatButton'
import { X } from 'lucide-react'
import clsx from 'clsx'
import FileNameWithCopy from '@/components/FileNameWithCopy'
import { useRenameDocument } from '@/hooks/useRenameDocument'
import { useTranslation } from 'react-i18next'
import { useAppStore } from '@/store'

const InterviewViewer = () => {
  const { doc_id } = useParams()
  const { t } = useTranslation(['data_catalog', 'document_viewer'])
  const [searchParams] = useSearchParams()
  // Prefer ?boardName= query param; fall back to active board in store so
  // the page title still shows the board name when the link was built
  // without the query param.
  const boardNameFromStore = useAppStore((s) => s.board?.name)
  const boardName = searchParams.get('boardName') || boardNameFromStore || null
  const navigateBack = useNavigateBack()
  const [document, setDocument] = useState<DocumentRecord | null>(null)
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [chatOpen, setChatOpen] = useState(false)

  useEffect(() => {
    const fetchDocument = async () => {
      if (!doc_id) return

      setLoading(true)
      try {
        const doc = await apiService.getDocument(doc_id)
        setDocument(doc)

        // Fetch the text content using downloadDocument
        const blob = await apiService.downloadDocument(doc_id)
        const text = await blob.text()
        setContent(text)
      } catch (err) {
        setError(err instanceof Error ? err.message : t('document_viewer:interview.loadError', { defaultValue: 'Failed to load document' }))
      } finally {
        setLoading(false)
      }
    }

    fetchDocument()
  }, [doc_id])

  const handleChatOpen = () => {
    setChatOpen(!chatOpen)
  }

  const renameDocument = useRenameDocument()
  const handleRename = (newName: string) => {
    if (!document) return
    void renameDocument(doc_id, newName, {
      previousName: document.name,
      applyName: (n) => setDocument((d) => (d ? { ...d, name: n } : d)),
    })
  }

  const closeAction = {
    onClick: navigateBack,
    element: (
      <button
        type="button"
        className="p-2 text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-accent rounded-lg transition-colors"
        title={t('common:buttons.close')}
      >
        <X className="w-5 h-5" />
      </button>
    ),
  }

  if (error) {
    return (
      <BoardLayout
        name={boardName || t('nav:menu.data', 'Data')}
        backUrl={''}
        handleBackCallback={navigateBack}
        actions={[closeAction]}
      >
        <div className="p-4 text-red-500">{error}</div>
      </BoardLayout>
    )
  }

  return (
    <BoardLayout
      name={boardName || t('nav:menu.data', 'Data')}
      backUrl={''}
      handleBackCallback={navigateBack}
      actions={[closeAction]}
    >
      <div className="flex h-full w-full">
        <div
          className={clsx(
            'h-full relative transition-all',
            chatOpen ? 'w-9/12' : 'w-full'
          )}
        >
          <div className="h-full flex flex-col gap-4 p-4 min-h-0">
            <div className="shrink-0">
              {document?.name ? (
                <FileNameWithCopy name={document.name} onRename={handleRename} />
              ) : (
                <h2 className="text-lg font-semibold">{t('document_viewer:common.loading', { defaultValue: 'Loading...' })}</h2>
              )}
            </div>

            {loading ? (
              <div className="flex-1 flex items-center justify-center">
                {t('document_viewer:common.loading', { defaultValue: 'Loading...' })}
              </div>
            ) : content ? (
              <div className="flex-1 border dark:border-dark-border rounded-lg overflow-auto min-h-0">
                <pre className="h-full p-4 font-mono whitespace-pre-wrap break-words text-sm">
                  {content}
                </pre>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                {t('document_viewer:interview.noContent', { defaultValue: 'No content available' })}
              </div>
            )}
          </div>
          <AiChatButton isOpen={chatOpen} onClick={handleChatOpen} />
        </div>

        <div
          className={clsx(
            'h-full overflow-hidden transition-all',
            chatOpen ? 'w-3/12' : 'w-0'
          )}
        >
          {doc_id && (
            <ChatInterface
              context_id={doc_id}
              context_type="document"
              onClose={handleChatOpen}
              chatMode="canvas"
            />
          )}
        </div>
      </div>
    </BoardLayout>
  )
}

export default InterviewViewer
